import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';

/*
 * Types.
 */

export enum TimelineSortOrdersEnum {
  OLDEST_FIRST = 'date',
  NEWEST_FIRST = '-date',
}

/*
 * Props.
 */

interface TimeLineEditSortOrderProps {
  sortOrder: TimelineSortOrdersEnum;
  setSortOrder: (arg: TimelineSortOrdersEnum) => void;
}

/*
 * Style.
 */

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  width: fit-content;
`;

const StyledSortOrderDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  margin-right: 15px;
`;

/*
 * Component.
 */

export const TimelineEditSortOrder: FC<TimeLineEditSortOrderProps> = ({sortOrder, setSortOrder}) => {
  const oldestText = <Trans id="analytics-timeline-sort-order-oldest-first">Oldest</Trans>;
  const newestText = <Trans id="analytics-timeline-sort-order-newest-first">Newest</Trans>;

  return (
    <StyledPopoverCoordinator
      renderButton={(isOpen) => (
        <Button variant={ButtonVariantsEnum.LINK} hasHover={isOpen} size={VisualSizesEnum.SMALL}>
          <StyledSortOrderDiv>
            {sortOrder === TimelineSortOrdersEnum.OLDEST_FIRST ? oldestText : newestText}
            <Icon name="chevronDownSmall" />
          </StyledSortOrderDiv>
        </Button>
      )}
      renderModal={(popoverProps) =>
        renderSortOrderPopover(popoverProps, setSortOrder, sortOrder, oldestText, newestText)
      }
    />
  );
};

/*
 * Helpers.
 */

function renderSortOrderPopover(
  popoverProps: PopoverTransferProps,
  setSelectedSortOrder: (arg: TimelineSortOrdersEnum) => void,
  selectedSortOrder: TimelineSortOrdersEnum,
  oldestText: ReactNode,
  newestText: ReactNode,
) {
  const onItemSelect = (sortOrder: TimelineSortOrdersEnum) => {
    setSelectedSortOrder(sortOrder);
    // Close the dropdown
    popoverProps.onRequestClose();
  };
  return (
    <Dropdown anchor={popoverProps.anchor} onRequestClose={popoverProps.onRequestClose}>
      <DropdownList>
        {renderSortOrderDropdownItem(
          TimelineSortOrdersEnum.OLDEST_FIRST,
          oldestText,
          selectedSortOrder,
          onItemSelect,
        )}
        {renderSortOrderDropdownItem(
          TimelineSortOrdersEnum.NEWEST_FIRST,
          newestText,
          selectedSortOrder,
          onItemSelect,
        )}
      </DropdownList>
    </Dropdown>
  );
}

function renderSortOrderDropdownItem(
  sortOrder: TimelineSortOrdersEnum,
  itemText: ReactNode,
  selectedSortOrder: TimelineSortOrdersEnum,
  onSelect: (arg: TimelineSortOrdersEnum) => void,
) {
  const isChecked = selectedSortOrder === sortOrder;
  return (
    <DropdownItem onSelect={() => onSelect(sortOrder)} isChecked={isChecked}>
      <StyledDropdownItemContent isChecked={isChecked}>{itemText}</StyledDropdownItemContent>
    </DropdownItem>
  );
}
